/**
 * ResumeGeneratorPage — Phase 1 milestone
 *
 * Paste a job description → generate a tailored CV + cover letter from your
 * SAVED profile → see a truth-check scoring every claim (verifiable / inferred /
 * fabricated) so you can defend your CV in the interview.
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE as API, getToken } from '../config';
import { exportCvPdf, exportCvDocx, cvPreviewHtml, CvProfile } from '../lib/cvExport';

// Shape passed via router state when arriving from the Jobs page ("Tailor CV").
interface PrefillJob {
  job_title?: string;
  company?: string;
  job_description?: string;
}

interface ClaimVerdict {
  claim: string;
  tier: 'verifiable' | 'inferred' | 'fabricated';
  rationale: string;
  source: string | null;
}

interface TruthReport {
  score: number | null; // null = verification was unavailable (model flaked)
  counts: { verifiable: number; inferred: number; fabricated: number };
  claims: ClaimVerdict[];
  note?: string;  // present when score is null — a human explanation
  error?: string; // legacy; retained for older cached results
}

interface GenerateResult {
  tailored: {
    tailored_summary: string;
    skills_focus: string[];
    experience: { role: string; company: string; dates: string; location: string; bullets: string[] }[];
    cover_letter: string;
  };
  latex: string;
  cover_letter: string;
  truth_report: TruthReport | null;
}

const TIER_STYLE: Record<string, { badge: string; dot: string; label: string }> = {
  verifiable: { badge: 'bg-green-100 text-green-800', dot: 'bg-green-500', label: 'Verifiable' },
  inferred:   { badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500', label: 'Inferred' },
  fabricated: { badge: 'bg-red-100 text-red-800',     dot: 'bg-red-500',   label: 'Fabricated' },
};

function scoreColor(score: number): string {
  if (score >= 85) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

// A labelled 0-100 slider with end captions and a live hint.
function Slider({ label, value, onChange, ends, hint }: {
  label: string; value: number; onChange: (v: number) => void;
  ends: [string, string]; hint?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        <span className="text-xs font-semibold text-blue-600">{value}%</span>
      </div>
      <input type="range" min={0} max={100} value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-blue-600 mt-1" />
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{ends[0]}</span><span>{ends[1]}</span>
      </div>
      {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

// Human explanation of what the anonymity level currently hides (mirrors the
// backend thresholds in cvGenerator.applyAnonymity).
function anonHint(level: number): string {
  if (level <= 0) return 'Nothing hidden';
  const hides: string[] = [];
  if (level >= 15) hides.push('contact');
  if (level >= 35) hides.push('name→initials');
  if (level >= 55) hides.push('employers');
  if (level >= 75) hides.push('schools');
  if (level >= 90) hides.push('exact dates');
  return 'Hides: ' + hides.join(', ');
}

const ResumeGeneratorPage: React.FC = () => {
  // Prefill from the Jobs page if we navigated here via "Tailor CV".
  const location = useLocation();
  const prefill = (location.state as PrefillJob | null) || {};

  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [profile, setProfile] = useState<CvProfile | null>(null);
  const [mode, setMode] = useState<'tailored' | 'general'>('tailored');
  const [jobTitle, setJobTitle] = useState(prefill.job_title || '');
  const [company, setCompany] = useState(prefill.company || '');
  const [jd, setJd] = useState(prefill.job_description || '');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [tab, setTab] = useState<'cv' | 'cover' | 'truth'>('cv');

  // Tuning knobs — sent as `options`; the user moves them then hits Regenerate.
  const [fit, setFit] = useState(70);
  const [anonymity, setAnonymity] = useState(0);
  const [length, setLength] = useState(50);
  const [tone, setTone] = useState('professional');

  // One LLM call tailors, a second truth-checks — surface that instead of a
  // mute 10-30s spinner. Timings are approximate; the last stage just holds.
  const STAGES = mode === 'tailored'
    ? ['Reading your profile…', 'Tailoring it to the job…', 'Writing the cover letter…', 'Truth-checking every claim…']
    : ['Reading your profile…', 'Composing your general CV…', 'Truth-checking every claim…'];
  useEffect(() => {
    if (!loading) { setStage(0); return; }
    const t = setInterval(() => setStage(s => Math.min(s + 1, STAGES.length - 1)), 7000);
    return () => clearInterval(t);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const token = getToken;

  useEffect(() => {
    // Full profile (not just /exists) — the PDF/Word exports need the header
    // identity, education, and languages, which the generate result omits.
    fetch(`${API}/api/cv-profile/`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => { setProfile(d || null); setHasProfile(!!d); })
      .catch(() => setHasProfile(false));
  }, []);

  const generate = async () => {
    if (mode === 'tailored' && !jd.trim()) { toast.error('Paste a job description first.'); return; }
    setLoading(true);
    setResult(null);
    try {
      const options = { fit, anonymity, length, tone };
      const res = await fetch(`${API}/api/cv-profile/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(
          mode === 'general'
            ? { mode: 'general', run_truth_check: true, options }
            : { job_title: jobTitle, company, job_description: jd, run_truth_check: true, options }
        ),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Generation failed (${res.status})`);
      }
      const data: GenerateResult = await res.json();
      setResult(data);
      setTab('cv');
      toast.success('CV generated.');
    } catch (e: any) {
      toast.error(e.message ?? 'Generation failed.');
    } finally {
      setLoading(false);
    }
  };

  const copyLatex = () => {
    if (result?.latex) {
      navigator.clipboard.writeText(result.latex);
      toast.success('LaTeX copied to clipboard.');
    }
  };

  const downloadPdf = () => {
    if (!result) return;
    try {
      exportCvPdf(profile ?? {}, result.tailored, company || undefined);
    } catch (e: any) {
      toast.error(e.message ?? 'PDF export failed.');
    }
  };

  const downloadWord = async () => {
    if (!result) return;
    try {
      await exportCvDocx(profile ?? {}, result.tailored, company || undefined);
      toast.success('Word document downloaded.');
    } catch (e: any) {
      toast.error(e.message ?? 'Word export failed.');
    }
  };

  if (hasProfile === false) {
    return (
      <div className="max-w-xl mx-auto text-center py-20">
        <div className="text-5xl mb-4">📄</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Set up your profile first</h1>
        <p className="text-gray-500 mb-6">
          To generate a tailored CV, upload your CV so we have your profile to work from.
        </p>
        <Link to="/app/profile-setup" className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
          Go to profile setup →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Generate a CV</h1>
        <p className="text-gray-500 mt-1">
          {mode === 'tailored'
            ? 'Paste a job description. We tailor your saved profile and truth-check every claim.'
            : 'A polished general-purpose CV from your saved profile — no job description needed.'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Input */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-1 bg-gray-100 rounded-lg p-1">
              {([['tailored', 'Tailored to a job'], ['general', 'General CV']] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    mode === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {mode === 'tailored' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Job title</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Platform Engineer" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Company</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={company} onChange={e => setCompany(e.target.value)} placeholder="CloudCo" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Job description *</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[280px] font-mono"
                    value={jd} onChange={e => setJd(e.target.value)}
                    placeholder="Paste the full job description here…"
                  />
                </div>
              </>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600 space-y-2">
                <p>
                  We turn your saved profile into a clean, all-purpose CV highlighting your
                  strongest experience and skills — ready to use anywhere.
                </p>
                <p className="text-xs text-gray-400">
                  No cover letter in this mode (there's no target company). Every claim is still truth-checked.
                </p>
              </div>
            )}

            {/* Tuning knobs */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fine-tune</p>
              {mode === 'tailored' && (
                <Slider label="Fit to job" hint="How aggressively to tailor toward the JD" value={fit} onChange={setFit}
                  ends={['Balanced', 'Laser-focused']} />
              )}
              <Slider label="Anonymity" value={anonymity} onChange={setAnonymity}
                ends={['Full identity', 'Fully blind']}
                hint={anonHint(anonymity)} />
              <Slider label="Length" value={length} onChange={setLength}
                ends={['One page', 'Detailed']} />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tone</label>
                <select value={tone} onChange={e => setTone(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="professional">Professional</option>
                  <option value="impact">Impact (achievement-led)</option>
                  <option value="concise">Concise</option>
                  <option value="warm">Warm</option>
                </select>
              </div>
            </div>

            <button
              onClick={generate}
              disabled={loading}
              className="w-full px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {STAGES[stage]}
                </>
              ) : result ? 'Regenerate with these settings ↻'
                : mode === 'tailored' ? 'Generate tailored CV →' : 'Generate general CV →'}
            </button>
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-3">
          {!result ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl h-full min-h-[400px] flex items-center justify-center text-center p-8">
              <div>
                <div className="text-4xl mb-3">✍️</div>
                <p className="text-gray-500">
                  {mode === 'tailored'
                    ? 'Your tailored CV, cover letter, and truth report will appear here.'
                    : 'Your general CV and truth report will appear here.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {/* Tabs */}
              <div className="flex items-center border-b border-gray-200 px-2">
                {([['cv','CV'],['cover','Cover letter'],['truth','Truth check']] as const)
                  .filter(([k]) => k !== 'cover' || !!result.cover_letter)
                  .map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      tab === k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {label}
                    {k === 'truth' && result.truth_report && !result.truth_report.error &&
                      typeof result.truth_report.score === 'number' && (
                      <span className={`ml-1.5 font-bold ${scoreColor(result.truth_report.score)}`}>
                        {result.truth_report.score}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="p-5">
                {tab === 'cv' && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={downloadPdf} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
                        Save as PDF
                      </button>
                      <button onClick={downloadWord} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                        Download Word (.docx)
                      </button>
                      <button onClick={copyLatex} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                        Copy LaTeX source
                      </button>
                    </div>
                    {/* Document-style preview — what the PDF will look like. */}
                    <iframe
                      title="CV preview"
                      srcDoc={cvPreviewHtml(profile ?? {}, result.tailored, company || undefined)}
                      className="w-full h-[560px] bg-white border border-gray-200 rounded-lg"
                      sandbox=""
                    />
                  </div>
                )}

                {tab === 'cover' && (
                  <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
                    {result.cover_letter || 'No cover letter generated.'}
                  </div>
                )}

                {tab === 'truth' && (
                  <TruthPanel report={result.truth_report} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function TruthPanel({ report }: { report: TruthReport | null }) {
  if (!report) return <p className="text-sm text-gray-500">Truth check was skipped.</p>;
  if (report.error) return <p className="text-sm text-red-600">Truth check failed: {report.error}</p>;

  // Verification unavailable (the model returned unreadable output). The CV is
  // still valid — only the fact-check couldn't run. Offer a clear next step
  // rather than a broken "0" that reads as "everything is fabricated".
  if (report.score === null) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <p className="font-medium mb-1">⚠️ Couldn't verify this CV automatically</p>
        <p className="text-amber-700">
          {report.note ?? 'The fact-checker was unavailable this time. Your CV was generated normally — just click Regenerate to run the truth-check again.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Score header */}
      <div className="flex items-center gap-4">
        <div className={`text-4xl font-bold ${scoreColor(report.score)}`}>{report.score}</div>
        <div>
          <p className="text-sm font-medium text-gray-900">Defensibility score</p>
          <p className="text-xs text-gray-500">
            How much of this CV you can back up from your real profile.
          </p>
        </div>
      </div>

      {/* Counts */}
      <div className="flex gap-3">
        {(['verifiable', 'inferred', 'fabricated'] as const).map(t => (
          <div key={t} className="flex items-center gap-1.5 text-xs">
            <span className={`w-2 h-2 rounded-full ${TIER_STYLE[t].dot}`} />
            <span className="text-gray-600">{TIER_STYLE[t].label}: <strong>{report.counts[t]}</strong></span>
          </div>
        ))}
      </div>

      {report.counts.fabricated > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          ⚠️ {report.counts.fabricated} claim{report.counts.fabricated > 1 ? 's' : ''} could not be traced to your profile.
          Remove or substantiate {report.counts.fabricated > 1 ? 'them' : 'it'} before sending.
        </div>
      )}

      {/* Claim list */}
      <div className="space-y-2">
        {report.claims.map((c, i) => (
          <div key={i} className="border border-gray-100 rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-gray-800 flex-1">{c.claim}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${TIER_STYLE[c.tier].badge}`}>
                {TIER_STYLE[c.tier].label}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{c.rationale}</p>
            {c.source && <p className="text-xs text-gray-400 mt-0.5">Source: {c.source}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ResumeGeneratorPage;
